import $ from 'jquery';
const { registerBlockType } = wp.blocks;
const {
  RichText,
  MediaUpload,
  MediaUploadCheck,
  InspectorControls,
  URLInputButton,
} = wp.blockEditor;
const {
  Button,
  PanelBody,
  RadioControl,
  SelectControl,
  IconButton,
  ToggleControl,
} = wp.components;
const { Fragment } = wp.element;
const { __ } = wp.i18n;

const baseClass = 'custom-image';

const ImageUpload = ({ onChange, value, altText }) => {
  return (
    <MediaUploadCheck>
      <MediaUpload
        onSelect={(media) => onChange(media.url, media.alt)}
        allowedTypes={['image']}
        value={value}
        render={({ open }) => (
          <>
            {value && <img src={value} className={baseClass} alt={altText} />}
            <Button onClick={open} isPrimary isSmall>
              {value ? (
                <>Change Image</>
              ) : (
                <>
                  <IconButton icon="upload" />
                  Upload Image
                </>
              )}
            </Button>
          </>
        )}
      />
    </MediaUploadCheck>
  );
};

const ImageView = ({ image, altText }) =>
  image ? (
    <img src={image} className={baseClass} alt={altText || 'Image'} />
  ) : null;

registerBlockType('pg/columns-section-images', {
  title: __('PG Columns Section Images'),
  icon: 'screenoptions',
  category: 'layout',
  attributes: {
    sectionClass: {
      type: 'string',
      default: 'section-dark-secondary',
    },
    sectionTitle: {
      type: 'string',
      source: 'html',
      selector: 'h3',
    },
    columns: {
      type: 'array',
      default: [],
      source: 'query',
      selector: '.column',
      query: {
        image: {
          type: 'string',
          source: 'attribute',
          attribute: 'src',
          selector: 'img',
        },
        title: { type: 'string', source: 'text', selector: 'h4' },
        description: { type: 'string', source: 'html', selector: 'div' },
        altText: {
          type: 'string',
          source: 'attribute',
          attribute: 'alt',
          selector: 'img',
        },
        buttonUrl: {
          type: 'string',
          source: 'attribute',
          attribute: 'href',
          selector: 'a',
        },
        buttonText: {
          type: 'string',
          source: 'text',
          selector: 'a > span',
        },
        buttonClass: {
          type: 'string',
          source: 'attribute',
          attribute: 'class',
          selector: 'a',
        },
      },
    },
    columnsNumber: {
      type: 'string',
      default: '4',
    },
    cornerPositionClass: {
      type: 'string',
      default: 'corner-disabled',
    },
    cornerBackgroundClass: {
      type: 'string',
      default: 'corner-on-light-primary',
    },
  },
  edit: ({ attributes, setAttributes }) => {
    const {
      sectionClass,
      sectionTitle,
      columns,
      columnsNumber,
      cornerPositionClass,
      cornerBackgroundClass,
    } = attributes;

    const darkMode = sectionClass.includes('light');

    const addColumn = () => {
      const newColumn = {
        image: '',
        title: '',
        description: '',
        altText: '',
        buttonUrl: null,
        buttonClass: 'btn btn-solid',
        buttonText: 'See more',
      };
      setAttributes({ columns: [...[newColumn], ...columns] });
    };

    const updateColumn = (index, updates) => {
      const newColumns = columns.map((column, i) =>
        i === index ? { ...column, ...updates } : column
      );
      setAttributes({ columns: newColumns });
    };

    const removeColumn = (index) => {
      const newColumns = columns.filter((_, i) => i !== index);
      setAttributes({ columns: newColumns });
    };

    const onChangeSectionClass = (newClass) => {
      setAttributes({ sectionClass: newClass });
    };

    const onChangeCornerPositionClass = (newClass) => {
      setAttributes({ cornerPositionClass: newClass });
    };

    const onChangeCornerBackgroundClass = (newClass) => {
      setAttributes({ cornerBackgroundClass: newClass });
    };

    return (
      <Fragment>
        <InspectorControls>
          <PanelBody title={__('Column Settings')}>
            <Button isSecondary onClick={addColumn}>
              + Add Column
            </Button>
            <RadioControl
              label={__('Columns Number')}
              selected={columnsNumber}
              options={[
                { label: '4 Columns', value: '4' },
                { label: '3 Columns', value: '3' },
                { label: '2 Columns', value: '2' },
                { label: '1 Column', value: '1' },
              ]}
              onChange={(value) => setAttributes({ columnsNumber: value })}
            />
            <SelectControl
              label={__('Section Class')}
              value={sectionClass}
              options={[
                { label: 'Dark Secondary', value: 'section-dark-secondary' },
                { label: 'Dark Primary', value: 'section-dark-primary' },
                { label: 'Light Primary', value: 'section-light-primary' },
                { label: 'Light Secondary', value: 'section-light-secondary' },
              ]}
              onChange={onChangeSectionClass}
            />
            <SelectControl
              label={__('Corner Position')}
              value={cornerPositionClass}
              options={[
                { label: 'None', value: 'corner-disabled' },
                { label: 'Left-Top', value: 'corner-left-top' },
                { label: 'Left-Bottom', value: 'corner-left-bottom' },
                { label: 'Right-Top', value: 'corner-right-top' },
                { label: 'Right-Bottom', value: 'corner-right-bottom' },
              ]}
              onChange={onChangeCornerPositionClass}
            />
            <SelectControl
              label={__('Corner Background')}
              value={cornerBackgroundClass}
              options={[
                { label: 'Dark Primary', value: 'corner-on-dark-primary' },
                { label: 'Dark Secondary', value: 'corner-on-dark-secondary' },
                { label: 'Light Primary', value: 'corner-on-light-primary' },
                {
                  label: 'Light Secondary',
                  value: 'corner-on-light-secondary',
                },
              ]}
              onChange={onChangeCornerBackgroundClass}
            />
          </PanelBody>
        </InspectorControls>
        <section className={sectionClass}>
          <div
            className={`corner ${cornerPositionClass} ${sectionClass.replace(
              'section',
              'corner'
            )} ${cornerBackgroundClass}`}
          ></div>
          <div className="columns-section">
            <RichText
              tagName="h3"
              value={sectionTitle}
              onChange={(newTitle) => setAttributes({ sectionTitle: newTitle })}
              placeholder={__('Image Section Title')}
            />
            <div className={`columns-wrapper columns-${columnsNumber} columns`}>
              {columns.map((column, index) => (
                <div key={index} className={`column`}>
                  <ImageUpload
                    onChange={(image, altText) =>
                      updateColumn(index, { image, altText })
                    }
                    value={column.image}
                    altText={column.altText}
                  />
                  <RichText
                    tagName="h4"
                    value={column.title}
                    placeholder={__('Column Title')}
                    onChange={(newTitle) =>
                      updateColumn(index, { title: newTitle })
                    }
                  />
                  <RichText
                    tagName={'div'}
                    placeholder={__('Column Description')}
                    value={column.description}
                    onChange={(newDescription) =>
                      updateColumn(index, { description: newDescription })
                    }
                  />
                  <a
                    href={'#'}
                    title={column.buttonText}
                    className={`${column.buttonClass} ${
                      darkMode ? 'btn-dark' : 'btn-light'
                    }`}
                  >
                    <RichText
                      tagName="span"
                      placeholder={'See more'}
                      value={column.buttonText}
                      onChange={(newLabel) =>
                        updateColumn(index, { buttonText: newLabel })
                      }
                      keepPlaceholderOnFocus={true}
                    />
                    <URLInputButton
                      url={column.buttonUrl}
                      onChange={(newUrl) =>
                        updateColumn(index, { buttonUrl: newUrl })
                      }
                    />
                  </a>
                  <ToggleControl
                    label={__('Text Button')}
                    checked={
                      column.buttonClass &&
                      column.buttonClass.includes('btn-text')
                    }
                    onChange={(isButtonClass) =>
                      updateColumn(index, {
                        buttonClass: isButtonClass
                          ? 'btn btn-text'
                          : 'btn btn-solid',
                      })
                    }
                  />
                  <Button
                    isSmall
                    isPrimary
                    className="removeStaticButton"
                    onClick={() => removeColumn(index)}
                  >
                    {__('Remove Column')}
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </section>
      </Fragment>
    );
  },
  save: ({ attributes }) => {
    const {
      sectionClass,
      sectionTitle,
      columns,
      columnsNumber,
      cornerPositionClass,
      cornerBackgroundClass,
    } = attributes;

    const darkMode = sectionClass.includes('light');

    return (
      <section className={sectionClass}>
        <div
          className={`corner ${cornerPositionClass} ${sectionClass.replace(
            'section',
            'corner'
          )} ${cornerBackgroundClass}`}
        ></div>
        <div className="columns-section">
          {sectionTitle && <h3>{sectionTitle}</h3>}
          <div className={`columns-wrapper columns-${columnsNumber}`}>
            {columns.map((column, index) => (
              <div key={index} className={`column`}>
                <ImageView image={column.image} altText={column.altText} />
                <h4>{column.title}</h4>
                <div dangerouslySetInnerHTML={{ __html: column.description }} />
                {column.buttonUrl && (
                  <a
                    href={column.buttonUrl}
                    className={`${column.buttonClass} ${
                      darkMode ? 'btn-dark' : 'btn-light'
                    }`}
                  >
                    <span>{column.buttonText}</span>
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  },
});
